/**
 * WHICH open database editor a `db_editor` call drives (#352).
 *
 * `db_editor`'s args name no database — they used to go to "the active
 * editor". Every open workspace stays mounted, so on the `/pending` recovery
 * path (run after an await, by a session whose workspace may be hidden) that
 * answered for another session: session A's `ALTER TABLE` ran on session B's
 * database and A was told it succeeded. The worker now stamps each call with
 * its turn's target (`ClientToolTarget`: the session's workspace, plus the
 * file the message's editor-context chip named), and this picks the one
 * editor that may answer:
 *
 *   1. the chip's file, when it is open in that workspace — on screen or not
 *      (owner, 2026-10-15: the right database off screen is still the right
 *      database);
 *   2. else that workspace's ACTIVE database tab (no chip, or the chip named
 *      something other than a database) — D1: owner 2026-10-19, the fallback
 *      stands, a missing chip is not refused;
 *   3. else a clear error — never another workspace's editor.
 *
 * A call with no target (from a worker that stamps none, mid-deploy) keeps
 * the old rule, minus the ambiguity: exactly one active editor answers.
 *
 * Pure over its inputs, so the binding is testable without a DOM.
 */
import type { ClientToolTarget } from '../core/client-tool-target.js'
import { isFirebirdDbName } from '@papan/types'

export interface DbEditorEntry {
  /** Registry key — one per mounted viewer (the same file can be open twice). */
  key: string
  fileId: string
  fileName: string
  /** The active tab of its stack — its workspace's tab group, or (always)
   *  chat-home's single side editor. So one workspace can hold two. */
  active: boolean
  /** Rendered on screen right now (not inside a hidden deck). Best effort —
   *  only ever used to PREFER one copy of a twice-open file. */
  onScreen: boolean
  /** The workspace it renders in; `null` outside one. */
  workspaceId: string | null
}

export type DbEditorPick =
  | { kind: 'editor'; entry: DbEditorEntry }
  | { kind: 'error'; error: string }

/** A file name the database editor opens (`.db`, `.sqlite`, `.sqlite3`, or any Firebird extension —
 *  `FIREBIRD_DB_EXTS`, the one list the Firebird editor registers, `.gdb` included). */
export function isDatabaseFileName(name: string | undefined): boolean {
  return !!name && (/\.(db|sqlite3?)$/i.test(name) || isFirebirdDbName(name))
}

export function pickDbEditor(
  entries: DbEditorEntry[],
  target: ClientToolTarget | null,
  linkFor: (fileId: string, fileName: string) => string,
): DbEditorPick {
  const links = (open: DbEditorEntry[]) => open.map(e => `[${e.fileName}](${linkFor(e.fileId, e.fileName)})`).join(' or ')
  const inScope = target ? entries.filter(e => e.workspaceId === target.workspaceId) : entries

  if (target?.fileId) {
    // The same file can be open twice in one workspace (its deck and
    // chat-home's side editor): either copy is the right DATABASE, so the
    // copy on screen answers, else an active one, else any.
    const copies = inScope.filter(e => e.fileId === target.fileId)
    const bound = copies.find(e => e.onScreen) ?? copies.find(e => e.active) ?? copies[0]
    if (bound) return { kind: 'editor', entry: bound }
    if (isDatabaseFileName(target.fileName)) {
      const name = target.fileName!
      return {
        kind: 'error',
        error:
          `The database this conversation is about (${name}) is not open in its workspace. ` +
          `Show the user this link to open it, then call db_editor again: [${name}](${linkFor(target.fileId, name)})`,
      }
    }
    // The chip named something other than a database — fall through to the
    // workspace's active database tab.
  }

  // Two DIFFERENT active databases in scope (the workspace's active tab and
  // chat-home's side editor for the same project) — no chip to choose
  // between them, and a guess is how the wrong database got written.
  // The on-screen copy of a twice-open file answers; the Map only COUNTS the
  // distinct files (it keeps the last entry per key).
  const actives = inScope.filter(e => e.active).sort((x, y) => Number(y.onScreen) - Number(x.onScreen))
  const distinct = [...new Map(actives.map(e => [e.fileId, e])).values()]
  if (distinct.length > 1) {
    return {
      kind: 'error',
      error:
        `More than one database is open${target ? " in this conversation's workspace" : ''} (${distinct.map(e => e.fileName).join(', ')}). ` +
        'Ask the user to open the database they mean as the focused tab, with the file chip above their message switched on, and resend.',
    }
  }
  const active = actives[0]
  if (active) return { kind: 'editor', entry: active }

  const open = inScope.filter(e => !e.active)
  if (open.length > 0) {
    return {
      kind: 'error',
      error:
        `A database editor is open but not the active tab — it can only be ` +
        `controlled while focused. Show the user this link to switch to it, ` +
        `then call db_editor again once they confirm it is open: ${links(open)}`,
    }
  }
  return {
    kind: 'error',
    error: target
      ? 'No database file is open in the workspace this conversation belongs to. Ask the user to open the ' +
        'database file (.db / .sqlite / .fdb) there, then call db_editor again.'
      : 'No database file is open. Ask the user to open a database file ' +
        '(.db / .sqlite / .fdb) in the editor, then call db_editor again.',
  }
}
